/** One entry of a rail row's menu. `null` in a list draws a separator, as the prototype's menus do. */
export interface RailMenuEntry {
  readonly id: string;
  readonly title: string;
  readonly icon: string;
  readonly disabled?: boolean;
  /** Why the entry is disabled. */
  readonly reason?: string;
  /** The action is not built in this app yet: the entry stays visible and answers "Coming soon". */
  readonly comingSoon?: boolean;
  /** With `comingSoon`: also draws the entry greyed with "Coming soon" written on it, so a person sees it is not wired before clicking. */
  readonly showComingSoon?: boolean;
  readonly dangerous?: boolean;
  /** Set on a checkable entry only: whether it is ticked. */
  readonly checked?: boolean;
  /** Hover text, as the prototype's menu button title (context-menu.js:29). */
  readonly hint?: string;
}

export type RailMenuItem = RailMenuEntry | null;

/** The prototype's two workspace scopes (`data-space`): whose rail row a menu belongs to. */
export type RailScope = 'mine' | 'team';

/** The prototype's workspace rail rows (`data-filter`), whatever an app calls them internally. */
export type RailRow =
  | 'all'
  | 'inbox'
  | 'assigned'
  | 'shared'
  | 'members'
  | 'today'
  | 'overdue'
  | 'sent'
  | 'draft'
  | 'scheduled'
  | 'flagged'
  | 'vip'
  | 'followup'
  | 'snoozed'
  | 'following'
  | 'other'
  | 'bulk'
  | 'spam'
  | 'archive'
  | 'trash';

export type RailRowActionId =
  | 'new-item'
  | 'new-container'
  | 'read-all'
  | 'clear'
  | 'empty-trash'
  | 'trash-archive'
  | 'settings';

export interface RailRowMenuContext {
  readonly row: RailRow;
  /** Lower-case nouns, e.g. `email` / `folder`. */
  readonly itemNoun: string;
  readonly containerNoun: string;
  /** The app has a per-person read state, so the row offers "Mark all as read for me". */
  readonly readable: boolean;
  /** The actions this app already has for this row; every other entry says "Coming soon". */
  readonly live: ReadonlySet<RailRowActionId>;
  /** A reason per action that is present but not allowed for this person. */
  readonly refused?: Partial<Record<RailRowActionId, string>>;
  /** The app's own entries that the prototype lacks, kept beside it (additive rule), above View settings. */
  readonly extras?: readonly RailMenuEntry[];
}

export const RAIL_ROW_ICONS: Readonly<Record<RailRowActionId, string>> = {
  'new-item': 'sapphire-plus',
  'new-container': 'sapphire-folders',
  'read-all': 'sapphire-email',
  clear: 'sapphire-close',
  'empty-trash': 'sapphire-trash',
  'trash-archive': 'sapphire-trash',
  settings: 'sapphire-settings',
};

const CLEAR_TITLES: Partial<Record<RailRow, string>> = {
  flagged: 'Clear my flags',
  followup: 'Clear my follow-up reminders',
  snoozed: 'Clear my snoozes',
  following: 'Clear my subscriptions',
};

/** The menu of a workspace rail row: the prototype's `navigation()` order, labels and icons, for any app. */
export function railRowMenu(context: RailRowMenuContext): readonly RailMenuItem[] {
  const entry = (id: RailRowActionId, title: string, extra: Partial<RailMenuEntry> = {}): RailMenuEntry => {
    const comingSoon = !context.live.has(id);
    const reason = comingSoon ? undefined : context.refused?.[id];
    return { id, title, icon: RAIL_ROW_ICONS[id], comingSoon, disabled: !!reason, reason, ...extra };
  };

  const items: RailMenuItem[] = [
    entry('new-item', `New ${context.itemNoun}`),
    entry('new-container', `New ${context.containerNoun}`),
  ];
  if (context.readable) {
    items.push(null, entry('read-all', 'Mark all as read for me'));
  }
  const clearTitle = CLEAR_TITLES[context.row];
  if (clearTitle) {
    items.push(entry('clear', clearTitle));
  }
  if (context.row === 'trash') {
    items.push(entry('empty-trash', 'Review items to delete permanently…', { dangerous: true }));
  }
  if (context.row === 'archive') {
    items.push(entry('trash-archive', 'Review archived items for Trash…', { dangerous: true }));
  }
  items.push(...(context.extras ?? []));
  items.push(null, entry('settings', 'View settings…'));
  return items;
}
